<!--
Copyright (c) 2023-2026, NVIDIA CORPORATION.  All rights reserved.

NVIDIA CORPORATION and its licensors retain all intellectual property
and proprietary rights in and to this software, related documentation
and any modifications thereto.  Any use, reproduction, disclosure or
distribution of this software and related documentation without an express
license agreement from NVIDIA CORPORATION is strictly prohibited.
-->

<!-- Blurred background container that extends behind app bar -->
@if (!busyLoadingMarquee && !errorVisible && glassUi) {
    <div
        class="marquee-blurred-background"
        [class.mobile-view]="mobileMode"
        [class.tv-mode]="tvMode"
        [class.phone-marquee]="phoneMarqueeEnabled"
        [style.--scrim-color]="scrimColor"
        [style.--scrim-color-right]="scrimColorRight"
    >
        <div class="marquee-bg-image" [style.background-image]="'url(' + marqueeImageUrl + ')'"></div>
        <div class="marquee-bg-lr-gradient"></div>
        <div class="marquee-bg-bottom-gradient"></div>
    </div>
}

@if (!busyLoadingMarquee && !errorVisible) {
    <div
        #marqueeContainer
        class="marquee-container"
        [ngClass]="{
            'mobile-view': mobileMode,
            'tv-mode': tvMode,
            'phone-marquee': phoneMarqueeEnabled,
            'paginator-navigation': enablePaginatorNavigation,
            'full-bleed-item': isTileTypeFullBleedImage,
            'full-bleed-item-not-first-position': isTileTypeFullBleedImageNotAtFirst,
            'no-bg-overhang': backgroundOverhangEnabled === false,
            'custom-bg': containerCustomBackground,
            'fallback-image-item': tileIsShowingFallbackImage
        }"
        (swipeleft)="swipeLeft()"
        (swiperight)="swipeRight()"
        nvSpatialNavigationGroup
        [style.--scrim-color]="scrimColor"
        [style.--dismiss-ratio]="dismissRatio"
        [attr.tabindex]="tabIndexValue"
    >
        @for (tile of marqueeData; track tile) {
            <div
                [ngClass]="{
                    fadeInMarquee: tile.isVisible && enableAnimation,
                    'padded-image-container': tile.imageType === tileOption.PaddedImage && !tileIsShowingFallbackImage,
                    'full-bleed-image-container':
                        tile.imageType === tileOption.FullBleedImage || tileIsShowingFallbackImage,
                    hidden: enableContentVisibilityOptimization && !tile.isVisible
                }"
            >
                @if (tile.isVisible) {
                    <img
                        elementtiming="marqueeTile"
                        [src]="marqueeImageUrl"
                        (load)="marqueeImageLoadComplete()"
                        (error)="fallbackToPlaceholderImage()"
                        alt=""
                        role="presentation"
                        class="marquee-tile-image"
                        [ngClass]="{
                            'no-mask': imageMaskDisabled
                        }"
                    />
                }
            </div>
        }
        @for (tile of marqueeData; track tile) {
            <div>
                @if (tile.isVisible && tile.app.promoTag) {
                    <div class="marquee-promo-tag-container" fxLayout="row" fxLayoutAlign="start center">
                        <div class="marquee-promo-tag-border"></div>
                        <div class="marquee-promo-tag" dir="auto">{{ tile.app.promoTag }}</div>
                    </div>
                }
            </div>
        }
        @if (!phoneMarqueeEnabled || glassUi) {
            @for (tile of marqueeData; track tile) {
                <div
                    class="marquee-tile-gradient-container"
                    [ngClass]="{
                        fadeInMarqueeDescription: tile.isVisible && enableAnimation,
                        hidden: enableContentVisibilityOptimization && !tile.isVisible
                    }"
                >
                    @if (tile.isVisible) {
                        <div class="marquee-tile-gradients">
                            @if (tile.gradients.background && !tile.isShowingFallbackImage) {
                                <div
                                    [ngClass]="{
                                        'marquee-tile-background-gradient': hasMarqueeScrim,
                                        'marquee-tile-background-fallback-gradient': !hasMarqueeScrim
                                    }"
                                ></div>
                            }
                            @if (tile.gradients.leftEdge && !tile.isShowingFallbackImage) {
                                <div class="marquee-tile-left-edge-gradient"></div>
                            }
                            @if (tile.gradients.leftMerge && !tile.isShowingFallbackImage) {
                                <div class="marquee-tile-left-merge-gradient"></div>
                            }
                            @if (tile.gradients.bottomEdge) {
                                <div class="full-bleed-up-down-gradient"></div>
                            }
                        </div>
                    }
                </div>
            }
        }
        @for (tile of marqueeData; track tile) {
            <div
                #marqueeTileContainerList
                class="marquee-tile-overlay-container"
                [ngClass]="{
                    fadeInMarqueeDescription: tile.isVisible && enableAnimation,
                    hidden: enableContentVisibilityOptimization && !tile.isVisible
                }"
            >
                @if (tile.isVisible) {
                    <div [ngClass]="{ 'marquee-tile-data': tile.contentOverlayType !== tileOption.FullBleedImage }">
                        @if (tile.contentOverlayType !== tileOption.FullBleedImage) {
                            <div class="overlay-content">
                                @if (!phoneMarqueeEnabled && appTitle) {
                                    <div
                                        class="font-header4 marquee-app-title"
                                        [style.color]="fontColor"
                                        role="heading"
                                        aria-level="2"
                                    >
                                        {{ appTitle }}
                                    </div>
                                }
                                @if (!appTitle) {
                                    <img
                                        alt=""
                                        role="presentation"
                                        elementtiming="appLogo"
                                        [src]="appLogoUrl"
                                        (error)="fallbackToAppLogo()"
                                        class="marquee-app-title"
                                    />
                                }
                                <div class="subtitle-container" [style.color]="fontColor" aria-hidden="true">
                                    @if (appSubtitle) {
                                        <div class="font-sub3">
                                            {{ appSubtitle }}
                                        </div>
                                    }
                                    @if (tile.app.additionalSubtitle) {
                                        <div dir="auto" class="font-sub3">
                                            {{ tile.app.additionalSubtitle }}
                                        </div>
                                    }
                                </div>
                                <button
                                    tabindex="0"
                                    mat-raised-button
                                    color="accent"
                                    class="marquee-tile-action-button font-button2 text-button-icon"
                                    (click)="onViewAppDetails($event, tile.app)"
                                    data-focus-loss-exempt
                                    [attr.aria-label]="getActionAriaLabel(tile)"
                                >
                                    {{ 'common.viewDetails' | translate }}
                                </button>
                            </div>
                        }
                        @if (tile.contentOverlayType === tileOption.FullBleedImage) {
                            <div class="full-bleed-overlay-content">
                                <div
                                    class="title-font"
                                    nvEllipsifiedTextWithTooltip
                                    tooltipClass="mat-tooltip-font-body3"
                                    [isMultiLineEllipsis]="true"
                                >
                                    {{ tile.app.title }}
                                </div>
                                <div
                                    class="body-font"
                                    nvEllipsifiedTextWithTooltip
                                    tooltipClass="mat-tooltip-font-body3"
                                    [isMultiLineEllipsis]="true"
                                    [innerHTML]="tile.app?.body"
                                ></div>
                                @if (tile.app?.action?.label?.length > 0) {
                                    <button
                                        mat-raised-button
                                        color="accent"
                                        class="marquee-tile-action-button font-button2"
                                        (click)="onActionClicked($event, tile.app.action, position, tile.app.id)"
                                        [attr.aria-label]="getActionAriaLabel(tile, tile.app?.action?.label)"
                                        nvProgressCircular
                                    >
                                        {{ tile.app.action.label }}
                                    </button>
                                }
                                @if (!!tile.app.action?.infoText) {
                                    <div class="info-font">
                                        {{ tile.app.action.infoText }}
                                    </div>
                                }
                            </div>
                        }
                    </div>
                }
            </div>
        }
        @if (position !== 0) {
            <button
                #leftButton
                mat-icon-button
                class="marquee-button marquee-button-prev"
                (click)="prevSlide()"
                tabindex="-1"
                [attr.aria-label]="previousButtonAriaLabel | translate"
            >
                <mat-icon class="marquee-button-icon" [svgIcon]="glassUi ? 'lt_arrow_glass' : 'lt_arrow'"></mat-icon>
            </button>
        }
        @if (position !== marqueeData.length - 1) {
            <button
                #rightButton
                mat-icon-button
                class="marquee-button marquee-button-next"
                (click)="nextSlide()"
                tabindex="-1"
                [attr.aria-label]="nextButtonAriaLabel | translate"
            >
                <mat-icon class="marquee-button-icon" [svgIcon]="glassUi ? 'rt_arrow_glass' : 'rt_arrow'"></mat-icon>
            </button>
        }
        @if (marqueeData?.length > 1) {
            <ul
                #paginator
                class="marquee-indicators"
                cdkMonitorSubtreeFocus
                [attr.tabindex]="enablePaginatorNavigation ? -1 : null"
                nvSpatialNavigationGroup
                navigateAction="focus"
                [attr.aria-label]="paginatorAriaLabel"
                role="tablist"
            >
                @for (selectedSlide of slideAccessor; track selectedSlide) {
                    <li
                        #paginatorList
                        [class.active]="selectedSlide === position"
                        (click)="goToSlide(selectedSlide)"
                        (focusin)="goToSlide(selectedSlide)"
                        [attr.tabindex]="enablePaginatorNavigation ? (selectedSlide === position ? 0 : -1) : null"
                        [attr.aria-label]="marqueeData[selectedSlide]?.app?.title"
                        role="tab"
                        [attr.aria-selected]="selectedSlide === position"
                        [attr.aria-current]="selectedSlide === position ? 'page' : null"
                    ></li>
                }
            </ul>
        }
        @if (useMarqueeScrim) {
            <div class="marquee-scrim" aria-hidden="true"></div>
        }
    </div>
}
@if (busyLoadingMarquee) {
    <div
        class="marquee-load-container"
        [ngClass]="{
            'no-bg-overhang': backgroundOverhangEnabled === false
        }"
    >
        <nv-hig-progress-circular></nv-hig-progress-circular>
    </div>
}
@if (errorVisible) {
    <div
        class="errorPlaceholder"
        fxLayoutAlign="center center"
        [ngClass]="{
            'no-bg-overhang': backgroundOverhangEnabled === false
        }"
    >
        <span class="font-header5"
            >{{ 'common.genericLoadError' | translate }} <br />{{ 'common.connectionError' | translate }}</span
        >
    </div>
}
